import React, { useRef, useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, Play, Pause, MessageCircle, Volume2, VolumeX, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { VideoCommentsDrawer } from '@/components/modals/VideoCommentsDrawer';
import { TimelineComment } from '@/types/video-validation';
import { useAuthStore } from '@/stores/authStore';
import { createCommentBatch } from '@/services/data/commentBatches';
import { getVideoCommentsWithBatches, mapDbCommentToTimelineComment, linkCommentsToBatch } from '@/services/data/videoComments';
import { createFeedbackPurchase } from '@/services/data/feedbackPurchases';
import { getS3DownloadUrl } from '@/services/data/s3Files';
import { notifyFeedbackWebhook } from '@/services/data/feedbackWebhook';
import { toast } from 'sonner';

interface FullscreenVideoPlayerProps {
  videoId: string;
  videoSrc: string;
  onClose: () => void;
  currentUserAvatar?: string;
  currentUserName?: string;
  videoVersion?: number;
}

// Helper function to get send button text
function getSendButtonText(isSending: boolean, isSecondBatch: boolean): string {
  if (isSending) {
    return "Envoi...";
  }
  if (isSecondBatch) {
    return "Envoyer quand même";
  }
  return "Envoyer mes retours";
}

// Helper function to format time display
function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

// Helper function to check if a comment is unsent
function isUnsentComment(comment: TimelineComment): boolean {
  const hasBatch = comment.batchId !== undefined && comment.batchId !== null;
  const markedAsSent = comment.isSent === true;
  return !hasBatch && !markedAsSent;
}

// Helper function to check if a comment has been sent
function hasBatchId(comment: TimelineComment): boolean {
  return comment.batchId !== undefined && comment.batchId !== null;
}

export function FullscreenVideoPlayer({
  videoId,
  videoSrc,
  onClose,
  currentUserAvatar,
  currentUserName,
  videoVersion = 1
}: FullscreenVideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  // Start muted for iOS autoplay compatibility - user can unmute after
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showControls, setShowControls] = useState(true);
  const [showCommentsDrawer, setShowCommentsDrawer] = useState(false);
  const [comments, setComments] = useState<TimelineComment[]>([]);
  const [isClosing, setIsClosing] = useState(false);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isLoadingComments, setIsLoadingComments] = useState(true);
  const [isReady, setIsReady] = useState(false);
  const [resolvedVideoUrl, setResolvedVideoUrl] = useState<string | null>(null);
  const [isLoadingVideo, setIsLoadingVideo] = useState(false);
  const [focusCommentId, setFocusCommentId] = useState<string | null>(null);
  const [activeMarkerId, setActiveMarkerId] = useState<string | null>(null);
  const hideControlsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  const { userId } = useAuthStore();

  // Resolve S3 key to signed URL if needed
  useEffect(() => {
    const resolveUrl = async () => {
      if (!videoSrc) return;
      // If it's already a full URL, use it directly
      if (videoSrc.startsWith('http://') || videoSrc.startsWith('https://') || videoSrc.startsWith('blob:')) {
        setResolvedVideoUrl(videoSrc);
        return;
      }
      // Otherwise, resolve via S3
      setIsLoadingVideo(true);
      try {
        const result = await getS3DownloadUrl(videoSrc, false);
        if (result.success && result.data?.url) {
          setResolvedVideoUrl(result.data.url);
        } else {
          console.error('Failed to resolve video URL:', result.error);
        }
      } catch (error) {
        console.error('Error resolving video URL:', error);
      } finally {
        setIsLoadingVideo(false);
      }
    };
    resolveUrl();
  }, [videoSrc]);

  // Load existing comments on mount
  useEffect(() => {
    const loadComments = async () => {
      if (!videoId) return;
      
      setIsLoadingComments(true);
      try {
        const result = await getVideoCommentsWithBatches(videoId, videoVersion);
        if (result.success && result.data) {
          const mappedComments = result.data.map(mapDbCommentToTimelineComment);
          setComments(mappedComments);
        }
      } catch (error) {
        console.error('Error loading comments:', error);
      } finally {
        setIsLoadingComments(false);
      }
    };

    loadComments();
  }, [videoId, videoVersion]);

  const startHideControlsTimer = () => {
    if (hideControlsTimeoutRef.current) {
      clearTimeout(hideControlsTimeoutRef.current);
    }
    hideControlsTimeoutRef.current = setTimeout(() => {
      if (isPlaying && !showCommentsDrawer) {
        setShowControls(false);
      }
    }, 3000);
  };

  useEffect(() => {
    if (isPlaying && !showCommentsDrawer) {
      startHideControlsTimer();
    } else {
      setShowControls(true);
      if (hideControlsTimeoutRef.current) {
        clearTimeout(hideControlsTimeoutRef.current);
      }
    }
    
    return () => {
      if (hideControlsTimeoutRef.current) {
        clearTimeout(hideControlsTimeoutRef.current);
      }
    };
  }, [isPlaying, showCommentsDrawer]);

  const handleMouseMove = () => {
    setShowControls(true);
    if (isPlaying && !showCommentsDrawer) {
      startHideControlsTimer();
    }
  };

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    
    if (isPlaying) {
      video.pause();
    } else {
      video.play().catch(console.error);
    }
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (video) {
      video.muted = !isMuted;
    }
    setIsMuted(!isMuted);
  };

  const handleTimeUpdate = () => {
    const video = videoRef.current;
    if (video) {
      setCurrentTime(video.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    const video = videoRef.current;
    if (video) {
      setDuration(video.duration);
      setIsReady(true);
    }
  };

  const handleCanPlay = () => {
    setIsReady(true);
    const video = videoRef.current;
    if (video && video.paused) {
      video.play()
        .then(() => setIsPlaying(true))
        .catch(() => {});
    }
  };

  const handlePlay = () => {
    setIsPlaying(true);
  };

  const handlePause = () => {
    setIsPlaying(false);
  };

  const handleVideoClick = () => {
    if (showCommentsDrawer) {
      // Close drawer and resume playback synchronously (required for iOS gesture chain)
      setShowCommentsDrawer(false);
      setIsPlaying(true);
      const video = videoRef.current;
      if (video) {
        video.play().catch(() => {});
      }
      return;
    }
    togglePlay();
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const video = videoRef.current;
    if (!video || duration === 0) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pos = (e.clientX - rect.left) / rect.width;
    video.currentTime = pos * duration;
  };

  const handleSeekTo = (time: number) => {
    const video = videoRef.current;
    if (video) {
      video.currentTime = time;
    }
  };

  const handleCloseRequest = () => {
    if (isPlaying) {
      setIsPlaying(false);
    }

    const unsentComments = comments.filter(isUnsentComment);

    if (unsentComments.length > 0) {
      setShowExitConfirm(true);
    } else {
      doClose();
    }
  };

  const doClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  const handleSendFeedbackAndClose = async () => {
    if (!userId) {
      console.error("User not authenticated");
      return;
    }

    setIsSending(true);
    try {
      const unsentComments = comments.filter(c => c.dbId && !c.batchId && !c.isSent);

      if (unsentComments.length === 0) {
        doClose();
        return;
      }

      // Check if this is a second batch (user already has sent comments)
      const hasSentBatchForClose = comments.some(hasBatchId);

      const batchId = await createAndLinkBatch(unsentComments);

      // If this is a second batch, create a purchase record
      if (hasSentBatchForClose) {
        await createFeedbackPurchase({
          user_id: userId,
          video_content_id: videoId,
          video_comment_batch_id: batchId,
          amount_cents: 5000,
          status: 'pending',
          notes: `Batch de retours complémentaires - ${unsentComments.length} commentaire(s)`
        });
      }

      setShowExitConfirm(false);
      doClose();
    } catch (error) {
      console.error("Error sending feedback:", error);
    } finally {
      setIsSending(false);
    }
  };

  // Helper to create batch and link comments
  const createAndLinkBatch = async (unsentComments: TimelineComment[]): Promise<string> => {
    const batchResult = await createCommentBatch({
      video_content_id: videoId,
      user_id: userId!,
      video_version: videoVersion
    });

    if (!batchResult.success || !batchResult.data) {
      throw new Error("Failed to create comment batch");
    }

    const batchId = batchResult.data.id;
    const commentIds = unsentComments
      .map(c => c.dbId)
      .filter((id): id is string => id !== undefined && id !== null);
    const linkResult = await linkCommentsToBatch(commentIds, batchId);

    if (!linkResult.success) {
      throw new Error("Failed to link comments to batch");
    }

    // Notify n8n webhook (fire & forget)
    notifyFeedbackWebhook(batchId);

    return batchId;
  };

  const handleCloseWithoutSending = () => {
    setShowExitConfirm(false);
    doClose();
  };

  const handleSendFeedbackFromDrawer = async () => {
    if (!userId || isSending) return;

    const unsentComments = comments.filter(c => isUnsentComment(c) && c.dbId);

    if (unsentComments.length === 0) return;

    // Check if this is a second batch (user already has sent comments)
    const hasSentBatchForDrawer = comments.some(c => c.isSent === true);

    setIsSending(true);
    try {
      const batchId = await createAndLinkBatch(unsentComments);
      const commentIds = new Set(
        unsentComments
          .map(c => c.dbId)
          .filter((id): id is string => id !== undefined && id !== null)
      );

      // If this is a second batch, create a purchase record
      if (hasSentBatchForDrawer) {
        await createFeedbackPurchase({
          user_id: userId,
          video_content_id: videoId,
          video_comment_batch_id: batchId,
          amount_cents: 5000,
          status: 'pending',
          notes: `Batch de retours complémentaires - ${unsentComments.length} commentaire(s)`
        });
        // Close video after successful purchase creation
        onClose();
        return;
      }

      setComments(prev => prev.map(c => {
        if (c.dbId && commentIds.has(c.dbId)) {
          return { ...c, isSent: true, batchId };
        }
        return c;
      }));
      setActiveMarkerId(null);
      toast.success(`${unsentComments.length} retour${unsentComments.length > 1 ? 's' : ''} envoyé${unsentComments.length > 1 ? 's' : ''} avec succès`);

    } catch (error) {
      console.error("Error sending feedback:", error);
      toast.error("Erreur lors de l'envoi des retours");
    } finally {
      setIsSending(false);
    }
  };

  const handleCommentsChange = useCallback((updatedComments: TimelineComment[]) => {
    setComments(updatedComments);
  }, []);

  const unsentCount = comments.filter(isUnsentComment).length;

  // Check if user already has sent comments (to detect second batch)
  const hasSentBatch = comments.some(hasBatchId);
  const isSecondBatch = hasSentBatch && unsentCount > 0;

  return createPortal(
    <div
      role="none"
      className={cn(
        "fixed inset-0 z-[200] bg-black flex flex-col transition-opacity duration-300",
        isClosing ? 'opacity-0' : 'opacity-100'
      )}
      onMouseMove={handleMouseMove}
    >
      {/* Close button */}
      <Button
        variant="ghost"
        size="icon"
        onClick={(e) => {
          e.stopPropagation();
          handleCloseRequest();
        }}
        className="absolute top-4 right-4 z-[210] text-white hover:bg-white/10 bg-black/50 backdrop-blur-sm rounded-full modal-safe-area-top"
      >
        <X className="h-5 w-5" />
      </Button>

      {/* Video */}
      <div 
        className={cn(
          "flex-1 flex items-center justify-center relative overflow-hidden transition-all duration-300",
          showCommentsDrawer && "items-end pt-4 pb-[28vh]"
        )}
        onClick={handleVideoClick}
      >
        {isLoadingVideo && (
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="h-8 w-8 text-white animate-spin" />
            <p className="text-muted-foreground text-sm">Chargement de la vidéo...</p>
          </div>
        )}

        {resolvedVideoUrl && !isLoadingVideo && (
          <video
            ref={videoRef}
            src={`${resolvedVideoUrl}#t=0.001`}
            className="h-full w-auto max-w-none object-cover"
            muted={isMuted}
            loop
            playsInline
            preload="auto"
            onTimeUpdate={handleTimeUpdate}
            onLoadedMetadata={handleLoadedMetadata}
            onCanPlay={handleCanPlay}
            onPlay={handlePlay}
            onPause={handlePause}
          >
            {/* Empty track for accessibility - captions not available for user-uploaded content */}
            <track kind="captions" srcLang="fr" label="Français" default />
          </video>
        )}

        {/* Loading state */}
        {!isReady && (
          <div className="absolute inset-0 flex items-center justify-center bg-black z-10">
            <div className="w-12 h-12 border-4 border-white/30 border-t-white rounded-full animate-spin" />
          </div>
        )}

        {/* Mute/Unmute button */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            toggleMute();
          }}
          className={cn(
            "absolute top-4 left-4 z-30 bg-black/50 backdrop-blur-sm rounded-full p-2 text-white hover:bg-black/70 transition-all modal-safe-area-top",
            showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'
          )}
        >
          {isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
        </button>
      </div>

      {/* Bottom Controls */}
      <div
        className={cn(
          "absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 pb-8 transition-opacity duration-300",
          showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'
        )}
      >
        {/* Comment markers */}
        {comments.filter(c => !c.parentId).length > 0 && (
          <div className="relative h-4 mb-1">
            {comments.filter(c => !c.parentId).map((comment) => {
              const position = duration > 0 ? (comment.timestamp / duration) * 100 : 0;
              return (
                <button
                  key={comment.id}
                  className="absolute -translate-x-1/2 bottom-0 cursor-pointer p-0 border-none bg-transparent"
                  style={{ left: `${position}%` }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSeekTo(comment.timestamp);
                    setShowCommentsDrawer(true);
                    setActiveMarkerId(comment.id);
                    setFocusCommentId(comment.id);
                    // Reset so clicking same marker again works
                    setTimeout(() => setFocusCommentId(null), 200);
                  }}
                  aria-label={`Commentaire à ${Math.floor(comment.timestamp / 60)}:${Math.floor(comment.timestamp % 60).toString().padStart(2, '0')}`}
                >
                  <div className={cn(
                    "w-2.5 h-2.5 rounded-full shadow-lg border border-black/30",
                    activeMarkerId === comment.id ? "bg-green-400" : comment.isSent ? "bg-amber-400" : "bg-white/50"
                  )} />
                </button>
              );
            })}
          </div>
        )}

        {/* Progress Bar */}
        <div
          className="w-full h-1 bg-white/30 rounded-full cursor-pointer mb-4"
          onClick={handleProgressClick}
        >
          <div
            className="h-full bg-white rounded-full relative"
            style={{ width: `${duration > 0 ? (currentTime / duration) * 100 : 0}%` }}
          >
            <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 w-3 h-3 bg-white rounded-full shadow-lg" />
          </div>
        </div>

        {/* Control Buttons */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={togglePlay}
              className="text-white hover:scale-110 transition-transform"
            >
              {isPlaying ? <Pause size={24} /> : <Play size={24} />}
            </button>
            <span className="text-white text-sm font-mono">
              {formatTime(currentTime)} / {formatTime(duration)}
            </span>
          </div>

          <button
            onClick={() => {
              const video = videoRef.current;
              if (video && isPlaying) {
                video.pause();
              }
              setShowCommentsDrawer(true);
            }}
            className="text-white hover:scale-110 transition-transform flex items-center gap-2"
          >
            <MessageCircle size={24} />
            {comments.length > 0 && (
              <span className="text-sm bg-white/20 px-2 py-0.5 rounded-full">
                {comments.length}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Comments Drawer */}
      {showCommentsDrawer && (
        <VideoCommentsDrawer
          videoId={videoId}
          videoVersion={videoVersion}
          currentTime={currentTime}
          duration={duration}
          initialComments={comments}
          isPlaying={isPlaying}
          currentUserAvatar={currentUserAvatar}
          currentUserName={currentUserName}
          initialPosition={comments.length > 0 ? "full" : "mid"}
          onClose={() => setShowCommentsDrawer(false)}
          onSeekTo={handleSeekTo}
          onTogglePlay={togglePlay}
          onCommentsChange={handleCommentsChange}
          onSendFeedback={handleSendFeedbackFromDrawer}
          isSendingFeedback={isSending}
          focusCommentId={focusCommentId}
          onCommentAdded={(id) => setActiveMarkerId(id)}
        />
      )}

      {/* Exit confirmation dialog */}
      <AlertDialog open={showExitConfirm} onOpenChange={setShowExitConfirm}>
        <AlertDialogContent className="bg-black/95 border-white/10 text-white z-[300]">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {isSecondBatch ? "Nouveau retour payant" : "Envoyer vos retours ?"}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-white/70 space-y-2">
              {isSecondBatch ? (
                <>
                  <p>
                    Vous avez déjà envoyé un premier lot de retours sur cette vidéo.
                  </p>
                  <p>
                    Ce nouveau lot ({unsentCount} retour{unsentCount > 1 ? 's' : ''}) sera considéré comme une <strong className="text-white">demande complémentaire</strong> et sera facturé <strong className="text-blue-400">50€ HT</strong>.
                  </p>
                </>
              ) : (
                <>
                  <p>
                    Un seul lot de demandes de modifications est inclus dans votre formule. Merci de vérifier que tous vos retours ont bien été enregistrés avant l'envoi.
                  </p>
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel 
              onClick={handleCloseWithoutSending}
              className="bg-white/10 text-white border-white/20 hover:bg-white/20"
            >
              Ne pas envoyer mes retours
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleSendFeedbackAndClose}
              disabled={isSending}
              className={isSecondBatch ? "bg-blue-500 text-white hover:bg-blue-600" : "bg-amber-500 text-white hover:bg-amber-600"}
            >
              {getSendButtonText(isSending, isSecondBatch)}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>,
    document.body
  );
}
